import { DateTime } from 'luxon'
import { ISODateTime } from './date'
import { compareByCodeUnit } from './stringUtils'

export const sortISODateTime =
  <T>(field: (element: T) => ISODateTime, order: 'ascending' | 'descending') =>
  (data1: T, data2: T): number => {
    return order === 'ascending'
      ? DateTime.fromISO(field(data2)).toMillis() - DateTime.fromISO(field(data1)).toMillis()
      : DateTime.fromISO(field(data1)).toMillis() - DateTime.fromISO(field(data2)).toMillis()
  }

/**
 * How a name sorts: case does not decide the order, so `apple` comes before `Zebra`.
 *
 * Only A to Z are lowercased, because that is all Mongo's `$toLower` does. A paginated list sorts on this key
 * stored in Mongo, and a migration fills it with `$toLower`, so the two must give the same bytes. Accented
 * letters keep their case and sort after `z`.
 */
export const nameSortKey = (name: string | null | undefined): string => (name ?? '').replace(/[A-Z]/g, (letter) => letter.toLowerCase())

/** `nameSortKey` for one character code: A to Z only. */
const lowercased = (code: number): number => (code >= 65 && code <= 90 ? code + 32 : code)

/**
 * Orders two names the way the stored {@link nameSortKey} does, so a list sorted in the browser matches a
 * page sorted by Mongo. Two names with the same key fall back to code-unit order, so the order never depends
 * on input order.
 */
export const compareNames = (a: string | null | undefined, b: string | null | undefined): number => {
  const left = a ?? ''
  const right = b ?? ''
  // Lowercases A to Z while comparing, as `nameSortKey` does, without building two new strings per call:
  // a sort calls this n log n times, and the mapping spreadsheet sorts thousands of rows on every edit.
  for (let i = 0; i < Math.min(left.length, right.length); i++) {
    const difference = lowercased(left.charCodeAt(i)) - lowercased(right.charCodeAt(i))
    if (difference !== 0) return difference
  }
  return left.length - right.length || compareByCodeUnit(left, right)
}

/** Compares items by the name `name` returns for each. */
export const byNameOf =
  <T>(name: (item: T) => string | null | undefined) =>
  (a: T, b: T): number =>
    compareNames(name(a), name(b))

export const byName = byNameOf<{ name: string }>((item) => item.name)

/** For select options and other `{ label }` items. */
export const byLabel = byNameOf<{ label: string }>((item) => item.label)

/** A sorted copy, never the list itself: callers pass props and query data. Fits a query's `select` as is. */
export const sortByName = <T extends { name: string }>(items: readonly T[]): T[] => [...items].sort(byName)

/** {@link sortByName} for items whose name is in another field. Define it once, outside a component, so a query's `select` keeps its reference. */
export const sortByNameOf =
  <T>(name: (item: T) => string | null | undefined) =>
  (items: readonly T[]): T[] =>
    [...items].sort(byNameOf(name))

export interface SortParams {
  sortOrder: 'ascending' | 'descending'
  sortBy: string
}
